'use client';

import { CustomLink } from '@/components/CustomLink';
import { Dropdown, Item } from '@/components/Dropdown';
import { UserMenu } from '@/components/UserMenu';
import { URL } from '@/constants/application-urls';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import styles from './HeaderMeta.module.css';
import useCart from '@/features/FeatureCart/client/hooks/useCart';
import { usePermissions } from '@/utils/hooks/usePermissions';
import { useContext, useMemo } from 'react';
import { AreaContext } from '@/utils/contexts/AreaContext';
import AreaContextType from '@/enums/AreaContextType';
import LanguageSwitch from '@/components/LanguageSwitch/LanguageSwitch';
import Permissions from '@/enums/Permissions';

interface HeaderMetaProps {
	username: string;
	dict: any;
	mobileOpen: boolean;
	setMobileOpen: Function;
	selectedBU: string | null;
	buData: Item[];
	handleBUChange: (item: Item) => void;
}

export default function HeaderMeta({
	username,
	dict,
	mobileOpen,
	setMobileOpen,
	buData,
	selectedBU,
	handleBUChange,
}: Readonly<HeaderMetaProps>) {
	const { cart } = useCart();
	const hasCartPermission = usePermissions(Permissions.CREATE_MY_CARTS);
	const areaContext = useContext(AreaContext);

	const displayCartItemsQuantity = useMemo(() => {
		if (cart && cart.totalLineItemQuantity > 9) {
			return '9+';
		}

		return cart?.totalLineItemQuantity;
	}, [cart]);

	return (
		<div className={`${styles.meta} ${areaContext === AreaContextType.MANAGE ? styles.spaceBetween : ''}`}>
			<div className={styles.logoWrapper}>
				<CustomLink href={URL.HOME} className={styles.logo}>
					<Image src="/Oerlikon_logo_white.svg" fill={true} alt="Oerlikon logo" priority />
				</CustomLink>
			</div>

			{areaContext === AreaContextType.DEFAULT && (
				<div className={styles.buWrapper}>
					<Dropdown
						data={buData}
						selected={selectedBU}
						selectedClassName="!text-customBlack-100 !text-white !fill-white"
						itemsWrapperClassName="w-[calc(100vw-15px)] !mr-0 "
						itemClassName="pl-[24px]"
						noBorder
						onChange={handleBUChange}
					/>
				</div>
			)}

			<div className="flex justify-end items-center">
				{hasCartPermission && areaContext === AreaContextType.DEFAULT && (
					<div className={styles.cartWrapper}>
						<CustomLink href={URL.CART} data-testid="header-cart-link">
							<div className={`${styles.cartBadge} ${cart && cart?.totalLineItemQuantity > 0 ? styles.isVisible : ''}`}>
								{displayCartItemsQuantity}
							</div>
							<ShoppingCartIcon className={'w-5'} />
						</CustomLink>
					</div>
				)}

				<div className={styles.desktopMenu}>
					<span className={styles.username}>{username}</span>
					<UserMenu dict={dict} />
				</div>
				<div
					className={`${styles.mobileMenu} ${mobileOpen ? styles.isActive : ''}`}
					onClick={() => setMobileOpen(!mobileOpen)}
				>
					<span className={styles.mobileMenuLine}></span>
				</div>
			</div>
		</div>
	);
}

export function HeaderMetaLogin({ dict }: { dict: any }) {
	return (
		<div className={`${styles.meta} ${styles.login}`}>
			<LanguageSwitch variant="tertiary" className={'col-end-7'} placeholder={dict.header.selectLanguage} />
		</div>
	);
}
